import type { EntityInput, Relationship, Source } from '../types/infrastructure'
import type { GlobalFacility, GlobalPowerRelationship, MapEvidenceSource } from '../types/global'
import { nationalSiteInputs } from './nationalExpansion.ts'

// Reviewed 2026-10-04. Pins are locality navigation anchors, not surveyed parcels.
// Utility agreements and regional grid resources do not establish exclusive wires.
const dateChecked = '2026-10-04'
const source = (title: string, publisher: string, url: string, date: string, confirms: string): Source => ({
  title, publisher, url, date, dateChecked, confirms,
})
const fairwater = source(
  'First Mount Pleasant datacenter facility is fully operational', 'Microsoft',
  'https://news.microsoft.com/source/2026/06/23/microsoft-completes-construction-on-first-datacenter-facility-in-mount-pleasant-wisconsin/',
  '2026-06-23', 'First facility operating; adjacent second facility under construction with a 2028 target.',
)
const fairwaterUtility = source(
  'Mount Pleasant project update — February 27, 2024 subsection', 'Microsoft Local',
  'https://local.microsoft.com/blog/mount-pleasant-datacenter-project-update/',
  '2024-02-27', 'Names We Energies as the campus substation developer; does not certify present commissioning.',
)
const googleDirectory = source(
  'Locations of Google Data Centers', 'Google', 'https://www.datacenters.google/locations/',
  'Undated live directory', 'West Memphis and Pine Island are listed as in development when checked.',
)
const westMemphisUtility = source(
  'Entergy Arkansas powers Google’s $4B investment in the state', 'Entergy Arkansas',
  'https://www.entergy.com/news/entergy-arkansas-powers-googles-4b-investment-in-the-state',
  '2025-10-02', 'Future West Memphis service uses Entergy Arkansas’s portfolio and transmission grid; proposed solar and storage are separate regional resources.',
)
const pineIsland = source(
  'Google is coming to Minnesota and advancing clean energy goals', 'Google',
  'https://blog.google/innovation-and-ai/infrastructure-and-cloud/global-network/data-center-pine-island/',
  '2026-02-24', 'Future Pine Island data center and Xcel partnership for regional wind, solar and storage resources.',
)
const pineIslandUtility = source(
  'Xcel Energy to Power New Google Data Center in Minnesota', 'Xcel Energy',
  'https://investors.xcelenergy.com/news-events/news-releases/news-details/2026/Xcel-Energy-to-Power-New-Google-Data-Center-in-Minnesota/default.aspx',
  '2026-02-24', 'Signed Electric Service Agreement required regulatory approval at announcement; current approval is not established by this source.',
)
const beaverDam = source(
  'Hello, Beaver Dam!', 'Meta', 'https://datacenters.atmeta.com/2025/11/hello-beaver-dam/',
  '2025-11-12', 'AI-optimized campus of more than 700,000 square feet and Alliant Energy partnership.',
)
const metaDirectory = source(
  'Global data center locations — Beaver Dam', 'Meta', 'https://datacenters.atmeta.com/all-locations/',
  'Undated live directory', 'Beaver Dam entry records 2025 groundbreaking and jobs once completed; does not claim operation.',
)
const beaverDamUtility = source(
  'Meta announces $1 billion Data Center Investment in Beaver Dam, Wisconsin Project', 'Alliant Energy',
  'https://www.alliantenergy.com/news/news-center/2025/11/111225-metabeaverdamannouncement',
  '2025-11-12', 'Beaver Dam Commerce Park campus, serving utility, planned grid investments and a 2027 online target.',
)
const lighthouseConstruction = source(
  'Port Washington data center project — construction update', 'City of Port Washington',
  'https://portdatacenter.com/', 'Undated live municipal project site',
  'Municipality reports construction began in December 2025 on the OpenAI, Oracle and Vantage campus.',
)
const lighthouseUtility = source(
  'Data centers won’t raise electricity bills', 'We Energies',
  'https://news.we-energies.com/data-centers-wont-raise-electricity-bills/', '2026-03-05',
  'Names planned utility service and regional grid investments for Lighthouse; tariff protections were proposed in this dated statement.',
)
const colossusUtility = source(
  'MLGW utility support for xAI update', 'Memphis Light, Gas and Water',
  'https://www.mlgw.com/images/content/files/pdf/new/5-5-25%20xAI%20Update.pdf', '2025-05-05',
  '150 MW supplied at Paul Lowry via TVA/MLGW grid as of May 5, 2025; a further 150 MW was a conditional request.',
)

type ReviewedCampus = EntityInput & {
  location: NonNullable<EntityInput['location']>
  locationPrecision: string
  projectStatus: 'Operating' | 'Under construction' | 'Planned'
}

const campuses: ReviewedCampus[] = [
  {
    id: 'microsoft-fairwater-mount-pleasant', name: 'Microsoft Fairwater — first Mount Pleasant facility', shortName: 'FAIRWATER WI',
    kind: 'site', layer: 'datacenter', status: 'Infrastructure', entityType: 'facility', systemStage: 'data-center',
    role: 'Operating Microsoft AI datacenter facility',
    description: 'Microsoft reported its first Fairwater facility fully operational in June 2026. The adjacent second facility is a separate construction phase with a 2028 target; operating status here applies only to the first facility. Electrical MW is not established by the inspected announcement.',
    location: { city: 'Mount Pleasant', state: 'Wisconsin', latitude: 42.71, longitude: -87.90 },
    locationPrecision: 'Approximate Mount Pleasant municipality anchor; not a campus parcel or substation location',
    projectStatus: 'Operating', stage: 'First facility operating; adjacent second facility under construction',
    completion: 'First facility operational; separate second facility target: 2028',
    tags: ['data-centers', 'microsoft', 'ai-compute', 'operating'], risk: ['Expansion delivery', 'Grid capacity'],
    sources: [fairwater, fairwaterUtility],
  },
  {
    id: 'google-west-memphis', name: 'Google West Memphis data center', shortName: 'GOOGLE AR',
    kind: 'site', layer: 'datacenter', status: 'Infrastructure', entityType: 'project', systemStage: 'data-center',
    role: 'Google cloud and AI data center in development',
    description: 'Google lists West Memphis as in development. Entergy Arkansas describes future supply from its portfolio and transmission grid. Proposed Jefferson County solar and storage are separate regional resources, not the campus load rating or an exclusive physical feed.',
    location: { city: 'West Memphis', state: 'Arkansas', latitude: 35.1465, longitude: -90.1848 },
    locationPrecision: 'Google directory locality coordinate; not a surveyed campus parcel',
    projectStatus: 'Planned', stage: 'In development; operating service not established',
    tags: ['data-centers', 'google', 'ai-compute', 'planned'], risk: ['Construction', 'Grid resources', 'Regulatory approval'],
    sources: [googleDirectory, westMemphisUtility],
  },
  {
    id: 'google-pine-island', name: 'Google Pine Island data center', shortName: 'GOOGLE MN',
    kind: 'site', layer: 'datacenter', status: 'Infrastructure', entityType: 'project', systemStage: 'data-center',
    role: 'Google data center in development with a signed service agreement',
    description: 'Google lists Pine Island as in development. Google and Xcel announced a service agreement and clean-energy framework for future regional wind, solar and long-duration storage. Those resource ratings are not campus electrical demand. Regulatory approval was required in February 2026; its current outcome is not established here.',
    location: { city: 'Pine Island', state: 'Minnesota', latitude: 44.20, longitude: -92.65 },
    locationPrecision: 'Approximate Pine Island city anchor; operator directory does not publish a facility coordinate',
    projectStatus: 'Planned', stage: 'In development; service agreement announced February 2026',
    tags: ['data-centers', 'google', 'planned', 'grid'], risk: ['Construction', 'Grid resources', 'Regulatory approval'],
    sources: [googleDirectory, pineIsland, pineIslandUtility],
  },
  {
    id: 'meta-beaver-dam', name: 'Meta Beaver Dam data center', shortName: 'META WI',
    kind: 'site', layer: 'datacenter', status: 'Infrastructure', entityType: 'project', systemStage: 'data-center',
    role: 'Meta AI-optimized campus under construction',
    description: 'Meta records a 2025 groundbreaking for its AI-optimized Beaver Dam campus in Alliant Energy’s Commerce Park. Alliant’s November 2025 announcement targets operations in 2027. The announced floor area exceeds 700,000 square feet; electrical MW and current energized load are not established.',
    location: { city: 'Beaver Dam', state: 'Wisconsin', latitude: 43.46, longitude: -88.84 },
    locationPrecision: 'Approximate Beaver Dam city anchor; Commerce Park campus parcel not independently geocoded',
    projectStatus: 'Under construction', stage: '2025 groundbreaking recorded; operation not established',
    completion: '2027 utility-announced target; not a guaranteed completion date',
    tags: ['data-centers', 'meta', 'ai-compute', 'under-construction'], risk: ['Construction delivery', 'Grid capacity'],
    sources: [beaverDam, metaDirectory, beaverDamUtility],
  },
]

export const reviewedUsSites: EntityInput[] = campuses

export const reviewedUsUtilities: EntityInput[] = [
  {
    id: 'we-energies', name: 'We Energies', shortName: 'WE ENERGIES', kind: 'company', layer: 'grid', status: 'Private',
    entityType: 'company', systemStage: 'grid', role: 'Wisconsin utility-infrastructure partner for Fairwater and Lighthouse',
    description: 'Microsoft’s dated update identifies We Energies as Fairwater substation developer. We Energies also describes future Lighthouse service. Neither record establishes a dedicated generating plant or the present tariff outcome.',
    tags: ['grid', 'utility', 'wisconsin'], sources: [fairwaterUtility, lighthouseUtility],
  },
  {
    id: 'entergy-arkansas', name: 'Entergy Arkansas', shortName: 'ENTERGY AR', kind: 'company', layer: 'grid', status: 'Private',
    entityType: 'company', systemStage: 'grid', role: 'Named future utility for Google West Memphis',
    description: 'Entergy Arkansas identifies future Google service through its existing generation portfolio and transmission grid. Its proposed regional solar and battery resources are not an exclusive direct feed or proof of operating campus demand.',
    tags: ['grid', 'utility', 'arkansas'], sources: [westMemphisUtility],
  },
  {
    id: 'xcel-energy', name: 'Xcel Energy', shortName: 'XCEL', kind: 'company', layer: 'grid', status: 'Public',
    entityType: 'company', systemStage: 'grid', role: 'Google Pine Island service-agreement and grid-resource partner',
    description: 'Xcel announced a signed Electric Service Agreement for Google Pine Island in February 2026. Proposed wind, solar and storage serve the regional grid. Approval was required at announcement; the current regulatory outcome is not verified here.',
    tags: ['grid', 'utility', 'minnesota'], sources: [pineIslandUtility, pineIsland],
  },
  {
    id: 'alliant-energy', name: 'Alliant Energy', shortName: 'ALLIANT', kind: 'company', layer: 'grid', status: 'Public',
    entityType: 'company', systemStage: 'grid', role: 'Named utility and grid-development partner for Meta Beaver Dam',
    description: 'Alliant identifies its Commerce Park as the campus location and describes utility, substation and transmission investment for future service. Renewable-energy matching does not establish hourly renewable-only physical supply.',
    tags: ['grid', 'utility', 'wisconsin'], sources: [beaverDamUtility, beaverDam],
  },
]

const operators: Record<string, { usId: string; globalSlugs: string[]; sourceIds: string[] }> = {
  'microsoft-fairwater-mount-pleasant': { usId: 'microsoft', globalSlugs: ['microsoft'], sourceIds: ['microsoft'] },
  // Global registry has Google DeepMind, not the Google campus operator; retain
  // Google's source identity without misattributing facility operation to DeepMind.
  'google-west-memphis': { usId: 'alphabet', globalSlugs: [], sourceIds: ['google'] },
  'google-pine-island': { usId: 'alphabet', globalSlugs: [], sourceIds: ['google'] },
  'meta-beaver-dam': { usId: 'meta', globalSlugs: ['meta'], sourceIds: ['meta'] },
}

type UtilityLink = {
  siteId: string
  utilityId: string
  label: Relationship['label']
  detail: string
  source: Source
  globalKind: GlobalPowerRelationship['kind']
  globalStatus: GlobalPowerRelationship['status']
}
const utilityLinks: UtilityLink[] = [
  {
    siteId: 'microsoft-fairwater-mount-pleasant', utilityId: 'we-energies', label: 'SUPPORTED BY',
    detail: 'Microsoft’s February 2024 update names We Energies as campus substation developer. This is historical utility-infrastructure evidence; present commissioning and service terms are not reverified, and no dedicated generating plant is established.',
    source: fairwaterUtility, globalKind: 'Utility service', globalStatus: 'Historically documented',
  },
  {
    siteId: 'google-west-memphis', utilityId: 'entergy-arkansas', label: 'SERVED BY',
    detail: 'Future utility service from Entergy Arkansas’s portfolio and transmission grid. The separately proposed 600 MW solar and 350 MW battery project in Jefferson County is not campus load or an exclusive physical feed; its current regulatory outcome was not reverified.',
    source: westMemphisUtility, globalKind: 'Planned grid connection', globalStatus: 'Planned',
  },
  {
    siteId: 'google-pine-island', utilityId: 'xcel-energy', label: 'PARTNERS WITH',
    detail: 'Signed future service agreement and clean-energy framework announced February 2026. Proposed 1,400 MW wind, 200 MW solar and 300 MW / 30 GWh storage are regional grid resources, not campus demand or direct exclusive supply. Approval was required at announcement; current approval is unverified.',
    source: pineIslandUtility, globalKind: 'Planned grid connection', globalStatus: 'Contracted',
  },
  {
    siteId: 'meta-beaver-dam', utilityId: 'alliant-energy', label: 'SERVED BY',
    detail: 'Named future serving utility with planned network, substation and transmission investment. The campus has a 2027 operations target. Clean/renewable electricity matching is not proof of hourly renewable-only physical supply or a dedicated generating plant.',
    source: beaverDamUtility, globalKind: 'Planned grid connection', globalStatus: 'Planned',
  },
]

export const reviewedUsRelationships: Relationship[] = [
  { id: 'stargate-wisconsin-we-energies', from: 'stargate-wisconsin', to: 'we-energies', type: 'grid', label: 'PARTNERS WITH', detail: 'We Energies identifies future Lighthouse utility service and regional generation/grid investment. Campus operations and current tariff approval are not established.', strength: 1, evidence: 'sourced', source: lighthouseUtility },
  ...campuses.map((site): Relationship => ({
    id: `${site.id}-${operators[site.id].usId}`, from: site.id, to: operators[site.id].usId,
    type: 'demand', label: 'OPERATED BY',
    detail: `The operator identifies this ${site.projectStatus === 'Operating' ? 'operating facility' : 'development project'}; the map anchor is approximate.`,
    strength: 1, evidence: 'sourced', source: site.sources[0],
  })),
  ...utilityLinks.map((link): Relationship => ({
    id: `${link.siteId}-${link.utilityId}`, from: link.siteId, to: link.utilityId, type: 'grid', label: link.label,
    detail: link.detail, strength: 1, evidence: 'sourced', source: link.source,
  })),
]

const globalEvidence = (item: Source): MapEvidenceSource => ({
  title: item.title, publisher: item.publisher, url: item.url, checkedAt: item.dateChecked ?? dateChecked,
})
const globalStatus: Record<ReviewedCampus['projectStatus'], GlobalFacility['status']> = {
  Operating: 'Operating', 'Under construction': 'Under construction', Planned: 'In development',
}

function existingUsCounterpart(id: string, operatorSlugs: string[], sourceIds: string[], description: string, sources: Source[]): GlobalFacility {
  const site = nationalSiteInputs.find((item) => item.id === id)
  if (!site?.location || !site.projectStatus || !(site.projectStatus in globalStatus)) {
    throw new Error(`Reviewed global counterpart requires a mapped U.S. site/status: ${id}`)
  }
  const status = globalStatus[site.projectStatus as ReviewedCampus['projectStatus']]
  return {
    slug: site.id, name: site.name, countrySlug: 'united-states',
    coordinates: [site.location.longitude, site.location.latitude],
    coordinatePrecision: site.locationPrecision ?? 'Approximate locality anchor; not a surveyed campus parcel',
    kind: 'Data center campus', status,
    verification: status === 'Operating' ? 'confirmed' : status === 'Under construction' ? 'under-construction' : 'announced',
    operatorSlugs, description, sourceIds, evidence: 'sourced', evidenceSources: sources.map(globalEvidence), usEntityId: site.id,
  }
}

// A single set of U.S. inputs controls both maps' coordinates, phase and sources.
export const reviewedUsGlobalFacilities: GlobalFacility[] = [...campuses.map((site): GlobalFacility => ({
  slug: site.id, name: site.name, countrySlug: 'united-states',
  coordinates: [site.location.longitude, site.location.latitude], coordinatePrecision: site.locationPrecision,
  kind: 'Data center campus', status: globalStatus[site.projectStatus],
  verification: site.projectStatus === 'Operating' ? 'confirmed' : site.projectStatus === 'Under construction' ? 'under-construction' : 'announced',
  operatorSlugs: operators[site.id].globalSlugs, description: site.description,
  sourceIds: operators[site.id].sourceIds, evidence: 'company-reported',
  evidenceSources: site.sources.map(globalEvidence), usEntityId: site.id,
})),
  existingUsCounterpart(
    'stargate-wisconsin', ['oracle'], ['openai', 'oracle'],
    'The City of Port Washington reports construction began in December 2025 on the OpenAI, Oracle and Vantage Lighthouse campus. Operations and energized campus MW are not established. We Energies describes future service and regional grid investment; no exclusive generating-plant feed is claimed.',
    [lighthouseConstruction, lighthouseUtility],
  ),
  existingUsCounterpart(
    'colossus-memphis', ['xai'], ['xai'],
    'Operating xAI campus at Paul Lowry Road, separate from the later Tulane Road facility. MLGW documented 150 MW grid service on May 5, 2025. A further 150 MW was a conditional request in that record; the present total is unverified. Utility allocation is not an onsite turbine-capacity measurement.',
    [colossusUtility],
  ),
]

export const reviewedUsGlobalPowerRelationships: GlobalPowerRelationship[] = [...utilityLinks.map((link) => ({
  consumerSlug: link.siteId,
  providerName: reviewedUsUtilities.find((utility) => utility.id === link.utilityId)!.name,
  kind: link.globalKind, status: link.globalStatus,
  description: link.detail, evidenceSource: globalEvidence(link.source),
})),
  {
    consumerSlug: 'stargate-wisconsin', providerName: 'We Energies', kind: 'Planned grid connection', status: 'Planned',
    description: 'We Energies’ March 5, 2026 statement describes future Lighthouse service supported by regional generation and grid investments. This is not an energized campus or an exclusive plant-to-campus route. The cost-protection tariff was proposed at that date; its current approval is not established here.',
    evidenceSource: globalEvidence(lighthouseUtility),
  },
  {
    consumerSlug: 'colossus-memphis', providerName: 'Memphis Light, Gas and Water / TVA grid', kind: 'Utility service', status: 'Historically documented',
    description: 'As of May 5, 2025, MLGW documented 150 MW supplied to Paul Lowry: 8 MW through an existing substation and 142 MW through a new substation. A further 150 MW remained a conditional request. The current total is unverified; no exclusive Allen or Southaven generating-plant connection is established.',
    evidenceSource: globalEvidence(colossusUtility),
  },
]
